import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Input, 
  Button, 
  Tabs, 
  message, 
  Divider, 
  Typography, 
  Avatar, 
  Statistic, 
  Descriptions,
  List,
  Tag,
  Spin,
  Empty
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  LockOutlined, 
  BookOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import { getStudentProfile, updateStudentProfile, changeStudentPassword } from '../../api/profile';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const StudentProfile = () => {
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const data = await getStudentProfile();
      setProfileData(data);
      
      // 设置表单初始值
      form.setFieldsValue({
        name: data.name,
        email: data.email,
        grade_level: data.grade_level,
        username: data.username
      });
    } catch (error) {
      message.error('获取个人信息失败');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (values) => {
    try {
      setSubmitting(true);
      await updateStudentProfile({
        name: values.name,
        email: values.email,
        grade_level: values.grade_level
      });
      message.success('个人信息更新成功');
      fetchProfileData(); // 重新获取数据
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        message.error(error.response.data.error);
      } else {
        message.error('更新个人信息失败');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordChange = async (values) => {
    try {
      setSubmitting(true);
      await changeStudentPassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });
      message.success('密码修改成功');
      passwordForm.resetFields();
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        message.error(error.response.data.error);
      } else {
        message.error('修改密码失败');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case true: return 'success';
      case false: return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '无数据';
    return dayjs(dateString).format('YYYY-MM-DD HH:mm:ss');
  };

  return (
    <div className="student-profile-container">
      <Title level={2}>个人信息</Title>
      <Paragraph>查看和管理您的个人信息、统计数据和最近活动</Paragraph>
      
      <Spin spinning={loading}>
        {profileData && (
          <Tabs defaultActiveKey="overview">
            <TabPane tab="个人概览" key="overview">
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <Card>
                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                      <Avatar size={80} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                      <Title level={4} style={{ marginTop: 16, marginBottom: 4 }}>{profileData.name}</Title>
                      <Text type="secondary">{profileData.username}</Text>
                      <Divider />
                      <Descriptions column={1}>
                        <Descriptions.Item label="电子邮箱">{profileData.email}</Descriptions.Item>
                        <Descriptions.Item label="年级">{profileData.grade_level || '未设置'}</Descriptions.Item>
                        <Descriptions.Item label="注册时间">{formatDate(profileData.created_at)}</Descriptions.Item>
                        <Descriptions.Item label="上次登录">{formatDate(profileData.last_login)}</Descriptions.Item>
                      </Descriptions>
                    </div>
                  </Card>
                </Col>

                <Col xs={24} md={16}>
                  <Card title="学习统计">
                    <Row gutter={[16, 16]}>
                      <Col xs={12} sm={6}>
                        <Statistic 
                          title="练习总数" 
                          value={profileData.stats.total_assignments || 0}
                          prefix={<BookOutlined />} 
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <Statistic 
                          title="已完成" 
                          value={profileData.stats.completed_exercises || 0}
                          prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                          valueStyle={{ color: '#52c41a' }}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <Statistic 
                          title="进行中" 
                          value={profileData.stats.in_progress_exercises || 0}
                          prefix={<ClockCircleOutlined style={{ color: '#1890ff' }} />}
                          valueStyle={{ color: '#1890ff' }}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <Statistic 
                          title="待完成" 
                          value={profileData.stats.pending_exercises || 0}
                          prefix={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
                          valueStyle={{ color: '#faad14' }}
                        />
                      </Col>
                    </Row>

                    <Divider />

                    <Row gutter={[16, 16]}>
                      <Col xs={12} sm={8}>
                        <Statistic 
                          title="逾期未完成" 
                          value={profileData.stats.overdue_exercises || 0}
                          valueStyle={{ color: '#ff4d4f' }}
                        />
                      </Col>
                      <Col xs={12} sm={8}>
                        <Statistic 
                          title="平均分数" 
                          value={profileData.stats.average_score || 0}
                          suffix="分"
                          precision={1}
                          prefix={<TrophyOutlined style={{ color: '#722ed1' }} />}
                          valueStyle={{ color: '#722ed1' }}
                        />
                      </Col>
                      <Col xs={24} sm={8}>
                        <Statistic 
                          title="完成率" 
                          value={profileData.stats.total_assignments ? 
                            Math.round((profileData.stats.completed_exercises / profileData.stats.total_assignments) * 100) : 0}
                          suffix="%"
                        />
                      </Col>
                    </Row>
                  </Card>

                  <Card title="最近提交记录" style={{ marginTop: 16 }}>
                    {profileData.recentSubmissions.length > 0 ? (
                      <List
                        itemLayout="horizontal"
                        dataSource={profileData.recentSubmissions}
                        renderItem={item => (
                          <List.Item
                            actions={[
                              <Tag color={getStatusColor(item.is_correct)}>
                                {item.is_correct ? '正确' : '错误'}
                              </Tag>,
                              <Text>{item.score} 分</Text>
                            ]}
                          >
                            <List.Item.Meta
                              title={<span>{item.question_title}</span>}
                              description={
                                <>
                                  <Text style={{ marginRight: 8 }}>练习: {item.exercise_title}</Text>
                                  <br />
                                  <Text type="secondary">提交时间: {formatDate(item.submitted_at)}</Text>
                                </>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    ) : (
                      <Empty description="暂无提交记录" />
                    )}
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab="编辑资料" key="edit">
              <Row justify="center">
                <Col xs={24} sm={20} md={16} lg={12}>
                  <Card>
                    <Form
                      form={form}
                      layout="vertical"
                      onFinish={handleProfileUpdate}
                    >
                      <Form.Item
                        name="username"
                        label="用户名"
                        rules={[{ required: true, message: '请输入用户名' }]}
                      >
                        <Input prefix={<UserOutlined />} disabled />
                      </Form.Item>

                      <Form.Item
                        name="name"
                        label="姓名"
                        rules={[{ required: true, message: '请输入姓名' }]}
                      >
                        <Input prefix={<UserOutlined />} placeholder="请输入姓名" />
                      </Form.Item>

                      <Form.Item
                        name="email"
                        label="电子邮箱"
                        rules={[
                          { required: true, message: '请输入电子邮箱' },
                          { type: 'email', message: '请输入有效的电子邮箱' }
                        ]}
                      >
                        <Input prefix={<MailOutlined />} placeholder="请输入电子邮箱" />
                      </Form.Item>

                      <Form.Item
                        name="grade_level"
                        label="年级"
                      >
                        <Input placeholder="请输入年级，例如：三年级" />
                      </Form.Item>

                      <Form.Item>
                        <Button type="primary" htmlType="submit" block loading={submitting}>
                          保存修改
                        </Button>
                      </Form.Item>
                    </Form>
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab="修改密码" key="password">
              <Row justify="center">
                <Col xs={24} sm={20} md={16} lg={12}>
                  <Card>
                    <Form
                      form={passwordForm}
                      layout="vertical"
                      onFinish={handlePasswordChange}
                    >
                      <Form.Item
                        name="currentPassword"
                        label="当前密码"
                        rules={[{ required: true, message: '请输入当前密码' }]}
                      >
                        <Input.Password 
                          prefix={<LockOutlined />}
                          placeholder="请输入当前密码" 
                        />
                      </Form.Item>

                      <Form.Item
                        name="newPassword"
                        label="新密码"
                        rules={[
                          { required: true, message: '请输入新密码' },
                          { min: 6, message: '密码长度至少为6个字符' }
                        ]}
                        hasFeedback
                      >
                        <Input.Password 
                          prefix={<LockOutlined />}
                          placeholder="请输入新密码" 
                        />
                      </Form.Item>

                      <Form.Item
                        name="confirmPassword"
                        label="确认新密码"
                        dependencies={['newPassword']}
                        hasFeedback
                        rules={[
                          { required: true, message: '请确认新密码' },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue('newPassword') === value) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error('两次输入的密码不一致'));
                            },
                          }),
                        ]}
                      >
                        <Input.Password 
                          prefix={<LockOutlined />}
                          placeholder="请确认新密码" 
                        />
                      </Form.Item>

                      <Form.Item>
                        <Button type="primary" htmlType="submit" block loading={submitting}>
                          修改密码
                        </Button>
                      </Form.Item>
                    </Form>
                  </Card>
                </Col>
              </Row>
            </TabPane>
          </Tabs>
        )}
      </Spin>
    </div>
  );
};

export default StudentProfile; 
